import React, { useEffect, useState } from 'react';
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useApi } from '../context/useApi';
import { useTranslation } from 'react-i18next';
import { IUsers, UsersFilters } from '../models/interfaces';
import type { AxiosResponse } from 'axios';
import { List } from '../shared/list';
import { useQuery } from 'react-query';
import { useMessage } from '../context/useMessage';
import { useServerlessList } from '../hook/useServerlessList';
import { DeleteOutline, Person, PersonOff } from '@mui/icons-material';
import UserFilters from '../components/UserFilters';
import { useDialog } from '../context/useDialog';
import CreateUserDialog from '../components/CreateUserDialog';
import { useLogged } from '../context/useLogged';
import { Navigate } from 'react-router-dom';

const FILTER_STORE_KEY = 'users';

const Users = () => {
  const { loggedUser } = useLogged();
  
  const { defaultApi } = useApi();
  const { t } = useTranslation();

  const [filter, setFilter] = useState<UsersFilters>();
  const { error, success } = useMessage();

  const { createDialog, closeDialog, createSimpleConfirm } = useDialog();

  const fetchUsers = () => {
    return defaultApi.usersGet({ params: filter }).then((resp: AxiosResponse<any>) => {
      if (resp.status === 200) {
        return resp.data.data ?? [];
      } else {
        return Promise.reject(new Error(t('error.error')));
      }
    });
  };

  const { data: users, isLoading: loading } = useQuery('user', fetchUsers, {
    refetchInterval: 20000,
    onError: (e: Error) => error({ title: e.message }),
    keepPreviousData: false,
    staleTime: 2,
  });

  const { count, handlePageChange, handlePageSizeChange, list, page, pageSize } = useServerlessList(
    {
      items: users ?? [],
      initialSize: 100,
    },
  );

  const [elements, setElements] = useState<IUsers[]>(users);

  useEffect(() => {
    setElements(() =>
      users ? users.filter((e: IUsers) => e.email.startsWith(filter?.email_prefix || '')) : [],
    );
  }, [filter, users]);

  if (loggedUser?.role !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  const clearFilters = () => {
    setFilter({});
  };

  const deleteUser = (email: string) => {
    defaultApi
      .userDelete({ data: { email } })
      .then((resp) => {
        if (resp.status === 200) {
          success({ message: t('success.deleteUser') });
        }
      })
      .catch(() => {
        error({ message: t('error.deleteUser') });
      })
      .finally(() => {
        fetchUsers().then((resp) => {
          setElements(() => {
            return resp.filter((e: IUsers) => e.email.startsWith(filter?.email_prefix || ''));
          });
        });
      });
  };

  const toggleEnableUser = (isEnabled: boolean, email: string) => {
    if (isEnabled) {
      defaultApi
        .userDisablePost({ data: { email } })
        .then((resp) => {
          if (resp.status === 200) {
            success({ message: t('success.disableUser') });
          }
        })
        .catch(() => {
          error({ message: t('error.disableUser') });
        })
        .finally(() => {
          fetchUsers().then((resp) => {
            setElements(() => {
              return resp.filter((e: IUsers) => e.email.startsWith(filter?.email_prefix || ''));
            });
          });
        });
    } else {
      defaultApi
        .userEnablePost({ data: { email } })
        .then((resp) => {
          if (resp.status === 200) {
            success({ message: t('success.enabledUser') });
          }
        })
        .catch(() => {
          error({ message: t('error.enableUser') });
        })
        .finally(() => {
          fetchUsers().then((resp) => {
            setElements(() => {
              return resp.filter((e: IUsers) => e.email.startsWith(filter?.email_prefix || ''));
            });
          });
        });
    }
  };

  return (
    <>
      <Stack
        direction="row"
        ml={'20px'}
        alignItems={'center'}
        justifyContent="space-between"
        spacing={1}
        marginBottom={2}
      >
        <Stack direction={'row'} alignItems={'center'} spacing={4} height={'56px'}>
          <Typography variant="h4" color="inherit">
            {t('user.title')}
          </Typography>
        </Stack>
      </Stack>
      <Stack marginBottom={'25px'}>
        <UserFilters
          clearFilters={clearFilters}
          filter={filter}
          defaultFilters={{}}
          setFilter={(filter) => setFilter(filter)}
          submit={fetchUsers}
        />
      </Stack>

      <List
        maxheight="calc(100vh - 20px)"
        list={elements}
        count={elements?.length || 0}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        gridTemplateColumns={'4fr 5fr 1.5fr 2fr 2fr 1fr'}
        keyGetter={(e: any) => e.email as string}
        // sortBy={convertStringsToSort(sort)}
        // onSortClick={onSortClick}
        onItemClick={() => {
          // navigate(`/${ROUTES.processList}#${e.id}`);
        }}
        actions={
          <Stack>
            <Button
              variant="contained"
              onClick={() =>
                createDialog({
                  children: (
                    <CreateUserDialog
                      closeDialog={() => {
                        closeDialog();
                        fetchUsers().then((resp) => {
                          setElements(() => {
                            return resp.filter((e: IUsers) =>
                              e.email.startsWith(filter?.email_prefix || ''),
                            );
                          });
                        });
                      }}
                    />
                  ),
                  open: true,
                })
              }
            >
              {t('actions.create')}
            </Button>
          </Stack>
        }
        actionsHeader={t('actions.title')}
        fields={[
          {
            title: t('user.email'),
            key: 'email',
          },
          {
            title: t('user.username'),
            key: 'username',
            tooltip: true,
          },
          {
            title: t('user.role'),
            key: 'role',
          },
          {
            title: t('user.status'),
            key: 'status',
          },
          {
            title: t('user.enabled'),
            key: 'has_review',
            cellRenderer: (e: any) => {
              return (
                <IconButton
                  color={e.enabled ? 'success' : 'error'}
                  onClick={() =>
                    createSimpleConfirm({
                      title: t('info.warning'),
                      message: e.enabled ? t('info.secureDisableUser') : t('info.secureEnableUser'),
                      onConfirm: () => toggleEnableUser(e.enabled, e.email),
                    })
                  }
                >
                  {e.enabled ? (
                    <Tooltip title={t('user.disable')}>
                      <Person />
                    </Tooltip>
                  ) : (
                    <Tooltip title={t('user.enable')}>
                      <PersonOff />
                    </Tooltip>
                  )}
                </IconButton>
              );
            },
          },
          {
            title: t('user.delete'),
            key: 'has_review',
            cellRenderer: (e: any) => {
              return (
                <IconButton
                  color={'error'}
                  onClick={() =>
                    createSimpleConfirm({
                      title: t('info.warning'),
                      message: t('info.sureToDeleteUser'),
                      onConfirm: () => deleteUser(e.email),
                    })
                  }
                >
                  <DeleteOutline />
                </IconButton>
              );
            },
          },
        ]}
        emptyListMessage={t('info.noResult')}
        keepRecentlyOpenedItem
      />
    </>
  );
};

export default Users;
